import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Chip,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Person as PersonIcon,
    DirectionsCar as CarIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/adminService';
import EmployeeDetailDialog from './EmployeeDetailDialog';

const InactiveWorkersView = () => {
    const [inactiveWorkers, setInactiveWorkers] = useState({ dispatchers: [], drivers: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    useEffect(() => {
        fetchInactiveWorkers();
    }, []);

    const fetchInactiveWorkers = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await adminAPI.user.getFiredWorkers();
            setInactiveWorkers({
                dispatchers: data.Dispatchers || data.dispatchers || [],
                drivers: data.Drivers || data.drivers || [],
            });
        } catch (err) {
            console.error('Error fetching inactive workers:', err);
            setError('Failed to load inactive workers. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fmt = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const openDetail = (emp, type) => {
        setSelectedEmployee(emp);
        setSelectedType(type);
        setDetailOpen(true);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" onClose={() => setError(null)}>
                {error}
            </Alert>
        );
    }

    const totalInactive = inactiveWorkers.dispatchers.length + inactiveWorkers.drivers.length;

    if (totalInactive === 0) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="300px">
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Inactive Workers
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    All workers are currently active.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Total inactive: {totalInactive}
            </Typography>

            {/* Inactive Dispatchers */}
            <Accordion defaultExpanded>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ backgroundColor: 'action.hover', '&:hover': { backgroundColor: 'action.selected' } }}
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        <PersonIcon color="error" />
                        <Typography variant="h6">Inactive Dispatchers</Typography>
                        <Chip label={inactiveWorkers.dispatchers.length} color="error" size="small" variant="outlined" />
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {inactiveWorkers.dispatchers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No inactive dispatchers.
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Final Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {inactiveWorkers.dispatchers.map(dispatcher => (
                                        <TableRow
                                            key={dispatcher.id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => openDetail(dispatcher, 'dispatcher')}
                                        >
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={500}>{dispatcher.name}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">{dispatcher.userName}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{dispatcher.email}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={fmt(dispatcher.endDate)} color="error" size="small" />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* Inactive Drivers */}
            <Accordion defaultExpanded sx={{ mt: 2 }}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ backgroundColor: 'action.hover', '&:hover': { backgroundColor: 'action.selected' } }}
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        <CarIcon color="error" />
                        <Typography variant="h6">Inactive Drivers</Typography>
                        <Chip label={inactiveWorkers.drivers.length} color="error" size="small" variant="outlined" />
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {inactiveWorkers.drivers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No inactive drivers.
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Final Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {inactiveWorkers.drivers.map(driver => (
                                        <TableRow
                                            key={driver.id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => openDetail(driver, 'driver')}
                                        >
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={500}>{driver.name}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">{driver.userName}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{driver.email}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={fmt(driver.endDate)} color="error" size="small" />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* Detail Dialog (read-only for inactive workers) */}
            {detailOpen && selectedEmployee && (
                <EmployeeDetailDialog
                    open={detailOpen}
                    onClose={() => setDetailOpen(false)}
                    employee={selectedEmployee}
                    employeeType={selectedType}
                    readOnly
                />
            )}
        </Box>
    );
};

export default InactiveWorkersView;
